import React, { useState, useEffect } from 'react';
import { FaPlus, FaMinus } from 'react-icons/fa';
import { GetConversionRates, SaveConversionRates } from '../../../../Actions/RequestActions';
import { Spinner } from 'react-bootstrap';

const MonazamSidebarBtns = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [pkrRate, setPkrRate] = useState({ pkr: '100', sar: 1 });
  const [usdRate, setUsdRate] = useState({ usd: 1, sar: '10' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    setLoading(true);
    GetConversionRates(
      (pkrData) => {
        // Ensure SAR is always 1, calculate PKR based on the rate
        // If pkrData.sar > 1, it means 1 PKR = sar SAR, so we need to calculate how many PKR = 1 SAR
        // If pkrData.pkr > 1, it means pkr PKR = 1 SAR, so use that
        const pkrValue = pkrData.pkr > 1 ? pkrData.pkr : (pkrData.sar > 1 ? 1 / pkrData.sar : 100);
        // Store as string to allow clearing
        setPkrRate({ pkr: String(pkrValue), sar: 1 });
      },
      (usdData) => {
        // Ensure USD is always 1, use the SAR value from the rate
        const sarValue = usdData.sar || usdData.usd || 10;
        // Store as string to allow clearing
        setUsdRate({ usd: 1, sar: String(sarValue) });
      }
    );
    setTimeout(() => {
      setLoading(false);
      setIsInitialLoad(false);
    }, 500);
  }, []);

  const toggleDropdown = () => {
    setShowDropdown(prev => !prev);
  };

  const handlePkrChange = (e) => {
    const inputValue = e.target.value;
    
    // Allow empty string for clearing
    if (inputValue === '') {
      setPkrRate({ pkr: '', sar: 1 });
      return;
    }
    
    // Only allow numbers and decimals, no negative
    if (!/^\d*\.?\d*$/.test(inputValue)) {
      return; // Invalid input, don't update
    }
    
    // Remove leading zeros (except for "0." or "0" or empty)
    let cleanedValue = inputValue;
    // Only remove leading zeros if there are digits after
    if (cleanedValue.match(/^0+[1-9]/)) {
      cleanedValue = cleanedValue.replace(/^0+/, '');
    }
    
    // SAR is fixed at 1, so just update PKR
    const newPkrRate = { pkr: cleanedValue, sar: 1 };
    setPkrRate(newPkrRate);
  };

  const handleUsdSarChange = (e) => {
    const inputValue = e.target.value;
    
    // Allow empty string for clearing
    if (inputValue === '') {
      setUsdRate({ usd: 1, sar: '' });
      return;
    }
    
    // Only allow numbers and decimals, no negative
    if (!/^\d*\.?\d*$/.test(inputValue)) {
      return; // Invalid input, don't update
    }
    
    // Remove leading zeros (except for "0." or "0" or empty)
    let cleanedValue = inputValue;
    // Only remove leading zeros if there are digits after
    if (cleanedValue.match(/^0+[1-9]/)) {
      cleanedValue = cleanedValue.replace(/^0+/, '');
    }
    
    // USD is fixed at 1, so just update SAR
    const newUsdRate = { usd: 1, sar: cleanedValue };
    setUsdRate(newUsdRate);
  };

  const handleSave = () => {
    // Validate inputs
    const pkrValue = parseFloat(pkrRate.pkr);
    const sarValue = parseFloat(usdRate.sar);
    
    if (!pkrRate.pkr || isNaN(pkrValue) || pkrValue <= 0) {
      setError('Please enter a valid PKR amount (greater than 0)');
      return;
    }
    
    if (!usdRate.sar || isNaN(sarValue) || sarValue <= 0) {
      setError('Please enter a valid SAR amount (greater than 0)');
      return;
    }
    
    setSaving(true);
    setError(null);
    // Ensure SAR in first row is 1 and USD in second row is 1
    const pkrRateToSave = { pkr: pkrValue, sar: 1 };
    const usdRateToSave = { usd: 1, sar: sarValue };
    SaveConversionRates(pkrRateToSave, usdRateToSave, (error) => {
      setSaving(false);
      if (error) {
        setError(error);
      } else {
        // Update state to reflect saved values (keep as strings for input)
        setPkrRate({ pkr: String(pkrValue), sar: 1 });
        setUsdRate({ usd: 1, sar: String(sarValue) });
      }
    });
  };

  return (
    <div>
      <div className="mb-3">
        <button
          className="btn label-text w-100 d-flex justify-content-between align-items-center sidebar-button"
          onClick={toggleDropdown}
        >
          <div className="d-flex align-items-center">
            Monazam to OPAP
            <img src="/assets/Frame 87.png" alt="Frame" className="me-2 img-fluid frame-icon" />
          </div>
          {showDropdown ? <FaMinus className="plus-icon" /> : <FaPlus className="plus-icon" />}
        </button>
        {showDropdown && (
          <div className="mt-2 border p-3 rounded bg-white">
            {loading ? (
              <div className="d-flex justify-content-center py-3">
                <Spinner animation="border" size="sm" style={{ color: "#198754" }} />
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="text-muted small currency-label">PKR</span>
                    <span className="fw-bold mx-2">=</span>
                    <span className="text-muted small currency-label">SAR</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="text"
                      value={pkrRate.pkr}
                      onChange={handlePkrChange}
                      className="form-control label-text text-center currency-input"
                      placeholder="0"
                      inputMode="decimal"
                    />
                    <span className="fw-bold">=</span>
                    <input
                      type="number"
                      value="1"
                      readOnly
                      disabled
                      className="form-control label-text text-center currency-input"
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="text-muted small currency-label">USD</span>
                    <span className="fw-bold mx-2">=</span>
                    <span className="text-muted small currency-label">SAR</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="number"
                      value="1"
                      readOnly
                      disabled
                      className="form-control label-text text-center currency-input"
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                    <span className="fw-bold">=</span>
                    <input
                      type="text"
                      value={usdRate.sar !== undefined && usdRate.sar !== '' ? usdRate.sar : ''}
                      onChange={handleUsdSarChange}
                      className="form-control label-text text-center currency-input"
                      placeholder="0"
                      inputMode="decimal"
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-end mt-3">
                  <button
                    className="btn btn-success btn-sm"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </button>
                </div>
                {error && (
                  <div className="alert alert-danger mt-2" role="alert">
                    <small>{error}</small>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MonazamSidebarBtns;
